/**
 * Formula page — Generate and explain Excel formulas.
 *
 * Two tabs:
 *   "Generer"   — natural language → formula
 *   "Expliquer" — formula → structured explanation
 *
 * The explain tab supports reading the formula directly from the active cell.
 */

import React, { useState } from 'react';
import {
  Button,
  Textarea,
  Text,
  TabList,
  Tab,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { MathFormulaRegular, LightbulbRegular } from '@fluentui/react-icons';
import { generateFormula, explainFormula } from '../services/api.client';
import { readExcelContext, readActiveFormula, getEffectiveFormulaLocale } from '../office';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';
import { ensureFormulaLocale } from '@excel-ai-studio/shared';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { FormulaCard } from '../components/features/FormulaCard';
import { ExplanationCard } from '../components/features/ExplanationCard';
import type { FormulaExplanationResult, FormulaParseMode } from '@excel-ai-studio/shared';

type FormulaTab = 'generate' | 'explain';
type ExplanationLevel = 'simple' | 'standard' | 'expert';
type DiagnosticSeverity = 'low' | 'medium' | 'high';
type DiagnosticItem = { text: string; severity: DiagnosticSeverity };
type ExpertDiagnostics = {
  performance: DiagnosticItem[];
  references: DiagnosticItem[];
  stability: DiagnosticItem[];
};

interface FormulaPageProps {
  initialTab?: FormulaTab;
}

export const FormulaPage: React.FC<FormulaPageProps> = ({ initialTab = 'generate' }) => {
  const [activeTab, setActiveTab] = useState<FormulaTab>(initialTab);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  // Generate tab state
  const [generatedFormula, setGeneratedFormula] = useState<string>();
  const [generatedExplanation, setGeneratedExplanation] = useState<string>();
  const [generatedWarnings, setGeneratedWarnings] = useState<readonly string[]>([]);
  const [generatedParseMode, setGeneratedParseMode] = useState<FormulaParseMode>();

  // Explain tab state
  const [explainResult, setExplainResult] = useState<FormulaExplanationResult>();
  const [explainedFormula, setExplainedFormula] = useState<string>();
  const [explainedParseMode, setExplainedParseMode] = useState<FormulaParseMode>();
  const [cellReadInfo, setCellReadInfo] = useState<string>();
  const [explanationLevel, setExplanationLevel] = useState<ExplanationLevel>('standard');

  const parseModeConfidence = (mode?: FormulaParseMode): number | undefined => {
    if (!mode) return undefined;
    if (mode === 'strict-json') return 90;
    if (mode === 'extracted-json') return 70;
    if (mode === 'legacy-fallback') return 45;
    return 25;
  };

  const suggestAlternative = (formula: string): string | undefined => {
    const up = formula.toUpperCase();
    if (/\bVLOOKUP\s*\(/.test(up)) {
      return 'Remplacer VLOOKUP par XLOOKUP/RECHERCHEX pour une recherche plus robuste.';
    }
    if (/\bSUMIFS?\s*\(/.test(up) || /\bSOMME\.SI/.test(up)) {
      return 'Alternative moderne: combiner FILTRER + SOMME pour une logique plus lisible.';
    }
    if (/\bIF\s*\(/.test(up) || /\bSI\s*\(/.test(up)) {
      return 'Alternative: utiliser LET pour clarifier les conditions complexes.';
    }
    return undefined;
  };

  const hasFullColumnRange = (formula: string): boolean => /\b[A-Z]{1,3}:[A-Z]{1,3}\b/.test(formula.toUpperCase());
  const hasMixedReferences = (formula: string): boolean => /\$[A-Z]{1,3}\d+|[A-Z]{1,3}\$\d+/.test(formula.toUpperCase());
  const hasNoAbsoluteReferences = (formula: string): boolean => !/\$[A-Z]{1,3}\$?\d+/.test(formula.toUpperCase());
  const hasLargeRowRange = (formula: string): boolean => /\b[A-Z]{1,3}\d{4,}:[A-Z]{1,3}\d{4,}\b/.test(formula.toUpperCase());
  const hasVolatileFunction = (formula: string): boolean =>
    /\b(NOW|TODAY|RAND|RANDBETWEEN|OFFSET|INDIRECT|MAINTENANT|AUJOURDHUI|ALEA|DECALER|INDIRECT)\s*\(/.test(
      formula.toUpperCase(),
    );

  const buildExplainView = (
    result: FormulaExplanationResult,
    formula: string,
    level: ExplanationLevel,
  ): FormulaExplanationResult => {
    const splitSentences = (text: string): string[] =>
      text
        .split(/(?<=[.!?])\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

    const extraWarnings = [...result.warnings];
    if (hasFullColumnRange(formula)) {
      extraWarnings.push(
        'La formule reference une colonne entiere (ex: A:A). Cela peut degrader les performances sur grands classeurs.',
      );
    }

    if (level === 'simple') {
      return {
        ...result,
        explanation: splitSentences(result.explanation).slice(0, 2).join(' '),
        breakdown: result.breakdown.slice(0, 2),
        suggestions: result.suggestions.slice(0, 2),
        warnings: extraWarnings,
      };
    }
    if (level === 'expert') {
      const expertHints: string[] = [
        'Mode expert: verifier les references absolues/relatives et la granularite des plages pour optimiser le recalcul.',
      ];
      const expertSuggestions = [...result.suggestions];
      const expertWarnings = [...extraWarnings];

      if (hasVolatileFunction(formula)) {
        expertHints.push(
          'Fonctions volatiles detectees: elles recalculent frequemment et peuvent ralentir le classeur.',
        );
        expertSuggestions.push(
          'Si possible, remplacer les fonctions volatiles par des valeurs calculees une seule fois ou des references stables.',
        );
      }

      if (hasMixedReferences(formula)) {
        expertHints.push(
          'References mixtes detectees ($A1, A$1): verifier le comportement attendu lors de la recopie.',
        );
      }

      if (hasNoAbsoluteReferences(formula) && /\b[A-Z]{1,3}\d+\b/.test(formula.toUpperCase())) {
        expertSuggestions.push(
          'Aucune reference absolue detectee: figer les cellules de parametre critiques avec $ si necessaire.',
        );
      }

      if (hasLargeRowRange(formula)) {
        expertWarnings.push(
          'Grande plage lignee detectee: envisager une table structuree Excel pour limiter le volume de recalcul.',
        );
      }

      const expertHint = `\n\n${expertHints.join(' ')}`;
      return {
        ...result,
        explanation: `${result.explanation}${expertHint}`,
        warnings: expertWarnings,
        suggestions: expertSuggestions,
      };
    }
    return {
      ...result,
      warnings: extraWarnings,
    };
  };

  const buildExpertDiagnostics = (formula: string): ExpertDiagnostics => {
    const diagnostics: ExpertDiagnostics = {
      performance: [],
      references: [],
      stability: [],
    };

    if (hasFullColumnRange(formula)) {
      diagnostics.performance.push({
        text: 'Utilise une colonne entiere (A:A): limiter la plage ou utiliser un tableau structure.',
        severity: 'high',
      });
    }
    if (hasLargeRowRange(formula)) {
      diagnostics.performance.push({
        text: 'Plage volumineuse detectee: reduire la zone analysee pour accelerer le recalcul.',
        severity: 'medium',
      });
    }
    if (hasMixedReferences(formula)) {
      diagnostics.references.push({
        text: 'References mixtes ($A1/A$1): verifier le comportement lors du glisser-recopier.',
        severity: 'medium',
      });
    }
    if (hasNoAbsoluteReferences(formula) && /\b[A-Z]{1,3}\d+\b/.test(formula.toUpperCase())) {
      diagnostics.references.push({
        text: 'Aucune reference absolue: figer les cellules parametres importantes avec $.',
        severity: 'low',
      });
    }
    if (hasVolatileFunction(formula)) {
      diagnostics.stability.push({
        text: 'Fonctions volatiles detectees: elles recalculent souvent et peuvent rendre le resultat instable.',
        severity: 'high',
      });
    }

    return diagnostics;
  };

  const resetState = () => {
    setInput('');
    setError(undefined);
    setGeneratedFormula(undefined);
    setGeneratedExplanation(undefined);
    setGeneratedWarnings([]);
    setGeneratedParseMode(undefined);
    setExplainResult(undefined);
    setExplainedFormula(undefined);
    setExplainedParseMode(undefined);
    setCellReadInfo(undefined);
    setExplanationLevel('standard');
  };

  const handleTabChange = (_event: unknown, data: { value: unknown }) => {
    setActiveTab(data.value as FormulaTab);
    resetState();
  };

  // ─── Generate ──────────────────────────────────────────

  const handleGenerate = async () => {
    if (!input.trim() || loading) return;
    const startedAt = Date.now();
    setError(undefined);
    setGeneratedFormula(undefined);
    setGeneratedExplanation(undefined);
    setGeneratedWarnings([]);
    setGeneratedParseMode(undefined);
    setLoading(true);

    try {
      let excelContext;
      try { excelContext = await readExcelContext(); } catch { /* not in Excel */ }
      const settings = getSettings();
      const locale = getEffectiveFormulaLocale();
      const result = await generateFormula({
        description: input.trim(),
        excelContext,
        language: settings.language,
        provider: settings.provider,
        model: settings.model,
      });
      // Client-side safety net: ensure formula matches the effective locale
      const formula = ensureFormulaLocale(result.formula, locale);
      setGeneratedFormula(formula);
      setGeneratedExplanation(result.explanation);
      setGeneratedWarnings(result.warnings);
      setGeneratedParseMode(result.parseMode);
      addHistoryEntry(
        'formula_generate',
        settings.provider,
        settings.model,
        input.trim(),
        true,
        undefined,
        {
          module: 'formulas',
          durationMs: Date.now() - startedAt,
          sheetName: excelContext?.sheetName,
          rangeAddress: excelContext?.selectedRange,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      const settings = getSettings();
      addHistoryEntry(
        'formula_generate',
        settings.provider,
        settings.model,
        input.trim(),
        false,
        (err as Error).message,
        {
          module: 'formulas',
          durationMs: Date.now() - startedAt,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Explain ───────────────────────────────────────────

  const handleReadFromCell = async () => {
    setCellReadInfo(undefined);
    setError(undefined);

    try {
      const info = await readActiveFormula();

      // Prefer the localized formula (French names) for display in the input field
      const displayFormula = info.formulaLocal ?? info.formula;
      if (displayFormula) {
        setInput(displayFormula);
        setCellReadInfo(`Formule lue depuis ${info.cellAddress}`);
      } else {
        // Cell has no formula
        const displayValue =
          info.cellValue === null || info.cellValue === undefined || info.cellValue === ''
            ? 'vide'
            : String(info.cellValue);
        setError(
          `La cellule ${info.cellAddress} ne contient pas de formule (valeur : ${displayValue}). ` +
          `Selectionnez une cellule contenant une formule (commencant par =).`,
        );
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleExplain = async () => {
    if (!input.trim() || loading) return;
    const startedAt = Date.now();
    setError(undefined);
    setExplainResult(undefined);
    setExplainedFormula(undefined);
    setExplainedParseMode(undefined);
    setLoading(true);

    try {
      const settings = getSettings();
      const formula = input.trim();
      const result = await explainFormula({
        formula,
        language: settings.language,
        provider: settings.provider,
        model: settings.model,
      });
      setExplainedFormula(formula);
      setExplainResult(result);
      setExplainedParseMode(result.parseMode);
      addHistoryEntry(
        'formula_explain',
        settings.provider,
        settings.model,
        formula,
        true,
        undefined,
        {
          module: 'formulas',
          durationMs: Date.now() - startedAt,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      const settings = getSettings();
      addHistoryEntry(
        'formula_explain',
        settings.provider,
        settings.model,
        input.trim(),
        false,
        (err as Error).message,
        {
          module: 'formulas',
          durationMs: Date.now() - startedAt,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ────────────────────────────────────────────

  return (
    <div>
      <TabList selectedValue={activeTab} onTabSelect={handleTabChange} size="small" style={{ marginBottom: 12 }}>
        <Tab value="generate" icon={<MathFormulaRegular />}>Generer</Tab>
        <Tab value="explain" icon={<LightbulbRegular />}>Expliquer</Tab>
      </TabList>

      <div className="card">
        <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)' }}>
          {activeTab === 'generate'
            ? 'Decrivez la formule souhaitee'
            : 'Entrez une formule a expliquer ou lisez-la depuis la cellule active'}
        </Text>
        {activeTab === 'explain' && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <Button
              size="small"
              appearance={explanationLevel === 'simple' ? 'primary' : 'secondary'}
              onClick={() => setExplanationLevel('simple')}
            >
              Simple
            </Button>
            <Button
              size="small"
              appearance={explanationLevel === 'standard' ? 'primary' : 'secondary'}
              onClick={() => setExplanationLevel('standard')}
            >
              Intermediaire
            </Button>
            <Button
              size="small"
              appearance={explanationLevel === 'expert' ? 'primary' : 'secondary'}
              onClick={() => setExplanationLevel('expert')}
            >
              Expert
            </Button>
          </div>
        )}
        <Textarea
          value={input}
          onChange={(_e, data) => setInput(data.value)}
          placeholder={
            activeTab === 'generate'
              ? 'Ex: Somme de la colonne B si colonne A = "Vente"'
              : 'Ex: =SOMME.SI.ENS(B:B;A:A;"Vente")'
          }
          resize="vertical"
          style={{ width: '100%', marginTop: 6, minHeight: 60 }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {activeTab === 'explain' && (
            <Button size="small" onClick={handleReadFromCell} disabled={loading}>
              Lire la cellule active
            </Button>
          )}
          <Button
            size="small"
            appearance="primary"
            onClick={activeTab === 'generate' ? handleGenerate : handleExplain}
            disabled={!input.trim() || loading}
          >
            {activeTab === 'generate' ? 'Generer' : 'Expliquer'}
          </Button>
        </div>

        {cellReadInfo && (
          <MessageBar intent="info" style={{ marginTop: 8, borderRadius: 4 }}>
            <MessageBarBody>{cellReadInfo}</MessageBarBody>
          </MessageBar>
        )}
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}

      {/* Generate result */}
      {generatedFormula && (
        <FormulaCard
          formula={generatedFormula}
          explanation={generatedExplanation}
          warnings={generatedWarnings}
          parseMode={generatedParseMode}
          confidenceScore={parseModeConfidence(generatedParseMode)}
          alternativeSuggestion={suggestAlternative(generatedFormula)}
          isLocal={getEffectiveFormulaLocale() === 'fr'}
          onExplain={(f) => {
            setActiveTab('explain');
            setInput(f);
            setGeneratedFormula(undefined);
            setGeneratedExplanation(undefined);
            setGeneratedWarnings([]);
            setGeneratedParseMode(undefined);
          }}
        />
      )}

      {/* Explain result */}
      {explainResult && explainedFormula && (
        (() => {
          const view = buildExplainView(explainResult, explainedFormula, explanationLevel);
          const expertDiagnostics = explanationLevel === 'expert'
            ? buildExpertDiagnostics(explainedFormula)
            : undefined;
          return (
        <ExplanationCard
          formula={explainedFormula}
          explanation={view.explanation}
          breakdown={view.breakdown}
          detectedFunctions={view.detectedFunctions}
          suggestions={view.suggestions}
          warnings={view.warnings}
          errors={view.errors}
          parseMode={explainedParseMode}
          confidenceScore={parseModeConfidence(explainedParseMode)}
          expertDiagnostics={expertDiagnostics}
        />
          );
        })()
      )}
    </div>
  );
};
